<template>
  <div class="campaign-quicklink">
    <p class="display-m campaign-quicklink-title">{{ title }}</p>
    <UiCarousel
      :slides-to-show-count="5"
      :slides-to-scroll-count="1"
      :is-swipeable="true"
      :arrow="carouselArrow"
      :show-dots="false"
      :breakpoints="breakpoints"
      ref="campaignQuicklinkCarousel"
    >
      <UiCarouselSlide
        ref="campaignQuicklinkSlides"
        v-for="(card, index) in campaignCards"
        :key="card.title"
        :data-index="index"
      >
        <UiCampaignCard
          v-bind="card"
          :image="getSmartImageAttrs(card.image)"
          @click="emit('campaign-quicklink-clicked', index)"
        >
          <!-- Handle modal within Campaign Quicklink component to ensure it overlays correctly, as the carousel's transform style affects the modal's display -->
          <button
            v-if="card.tnc"
            @click.prevent="showModal(card.tnc)"
            class="campaign-card-tnc body-s"
          >
            {{ card.tnc.label }}
          </button>
        </UiCampaignCard>
      </UiCarouselSlide>
    </UiCarousel>
    <UiModal
      v-if="isModalOpen && tncContent"
      :title="tncContent.label"
      @close="isModalOpen = false"
    >
      <template #body> {{ tncContent.content }} </template>
    </UiModal>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue'
import UiCarousel, {
  ArrowPosition,
  NavArrowsDisplayType,
  UiCarouselSlide,
} from '@sephora-asia/ui-carousel'
import UiCampaignCard from '@sephora-asia/ui-campaign-card'
import UiModal from '@sephora-asia/ui-modal'
import type { PropsCampaignQuicklink } from './types'
import type { PropsSmartImage } from '@sephora-asia/ui-smart-image'

defineProps<PropsCampaignQuicklink>()

interface Emits {
  (e: 'campaign-quicklink-impression', index: number): void
  (e: 'campaign-quicklink-clicked', index: number): void
}

const emit = defineEmits<Emits>()

const getSmartImageAttrs = (image: Omit<PropsSmartImage, 'aspectRatio'>) => ({
  ...image,
  aspectRatio: '4/3',
})

const isModalOpen = ref(false)
const tncContent = ref()
const campaignQuicklinkSlides = ref<InstanceType<typeof UiCarouselSlide>[]>([])
const campaignQuicklinkCarousel = ref(null)

const showModal = (tnc: { label: string; content: string }) => {
  tncContent.value = tnc
  isModalOpen.value = true
}

const carouselArrow = {
  position: ArrowPosition.WITHIN,
  displayType: NavArrowsDisplayType.SHOW_ON_HOVER,
  size: '',
}

// when update the breakpoints, need to update in css as well
const breakpoints = {
  0: {
    itemsToShow: 1.5,
    itemsToScroll: 1,
  },
  768: {
    itemsToShow: 3.5,
    itemsToScroll: 1,
  },
  1024: {
    itemsToShow: 4,
    itemsToScroll: 1,
  },
  1280: {
    itemsToShow: 4.5,
    itemsToScroll: 1,
  },
  1440: {
    itemsToShow: 5,
    itemsToScroll: 1,
  },
}

let observer: IntersectionObserver | null = null

onMounted(async () => {
  await nextTick()

  const handleIntersection = (entries: IntersectionObserverEntry[]) => {
    entries.forEach(entry => {
      const index = parseInt(entry.target.getAttribute('data-index') || '-1')
      if (entry.isIntersecting && index !== -1) {
        emit('campaign-quicklink-impression', index)
        observer?.unobserve(entry.target)
      }
    })
  }

  observer = new IntersectionObserver(handleIntersection, {
    threshold: 1.0,
  })

  campaignQuicklinkSlides.value?.forEach(slide => {
    observer?.observe(slide?.$el)
  })
})

onBeforeUnmount(() => {
  if (observer) {
    observer.disconnect()
  }
})
</script>

<style lang="scss" scoped>
.campaign-quicklink {
  overflow: hidden;

  &-title {
    margin-bottom: 24px;
  }

  .campaign-card-tnc {
    border: none;
    background-color: transparent;
    cursor: pointer;
    text-decoration: underline;
    padding: 0;
  }

  :deep(.carousel-container) {
    margin: 0;

    .arrow {
      @include media(mobile, tablet) {
        display: none;
      }
    }
  }

  :deep(.campaign-card) {
    width: auto;

    .campaign-card__content {
      text-align: left;
      background-color: var(--color-base-white);

      .campaign-card__cta {
        min-width: fit-content;
      }
    }
  }

  /* handle slides to be shown while loading base on breakpoints */
  @each $breakpoint,
    $value in (mobile: 1.5, tablet: 3.5, desktop: 4, widescreen: 4.5, fullhd: 5)
  {
    :deep(.carousel__slide) {
      @include media($breakpoint) {
        width: calc(100% / $value) !important;
      }
    }
  }

  @include media(mobile, tablet) {
    &-title {
      margin-bottom: 16px;
    }
  }
}
</style>
